import { request } from './_core'

// ── Professional Mode ─────────────────────────────────────────

export interface ProfessionalProfile {
  id:        string
  name:      string
  notes:     string | null
  createdAt: string
  updatedAt: string
}

export interface PlaybookItemData {
  id:           string
  playbookId:   string
  activitySlug: string
  config:       Record<string, unknown>
  sortOrder:    number
}

export interface PlaybookData {
  id:        string
  profileId: string
  name:      string
  createdAt: string
  updatedAt: string
  items:     PlaybookItemData[]
}

export interface PlaybookSessionItemData {
  id:             string
  sessionId:      string
  playbookItemId: string
  score:          number | null
  durationMs:     number | null
  moves:          number | null
  pairsTotal:     number | null
  pairsMatched:   number | null
  completedAt:    string | null
}

export interface PlaybookSessionData {
  id:           string
  profileId:    string
  playbookId:   string
  status:       'IN_PROGRESS' | 'LOCKED' | 'UNLOCKED'
  startedAt:    string
  completedAt:  string | null
  unlockedAt:   string | null
  items:        PlaybookSessionItemData[]
  playbookItems?: PlaybookItemData[]  // included on session start
}

export interface ProfileStats {
  evolution: Record<string, { date: string; score: number | null; durationMs: number | null }[]>
  summary:   { totalSessions: number; lastSessionAt: string | null }
}

export interface SubmitSessionItemParams {
  durationMs?:   number
  moves?:        number
  pairsTotal?:   number
  pairsMatched?: number
}

export const professional = {
  enable: () =>
    request<{ isProfessional: boolean }>('/professional/enable', { method: 'POST', body: '{}' }),

  disable: () =>
    request<{ isProfessional: boolean }>('/professional/disable', { method: 'POST', body: '{}' }),

  // Profiles
  listProfiles: () =>
    request<ProfessionalProfile[]>('/professional/profiles'),

  createProfile: (data: { name: string; notes?: string; pin: string }) =>
    request<ProfessionalProfile>('/professional/profiles', { method: 'POST', body: JSON.stringify(data) }),

  updateProfile: (id: string, data: { name?: string; notes?: string | null; pin?: string }) =>
    request<ProfessionalProfile>(`/professional/profiles/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),

  deleteProfile: (id: string) =>
    request<void>(`/professional/profiles/${id}`, { method: 'DELETE' }),

  // Playbooks
  listPlaybooks: (profileId: string) =>
    request<PlaybookData[]>(`/professional/profiles/${profileId}/playbooks`),

  createPlaybook: (profileId: string, data: { name: string }) =>
    request<PlaybookData>(`/professional/profiles/${profileId}/playbooks`, { method: 'POST', body: JSON.stringify(data) }),

  getPlaybook: (id: string) =>
    request<PlaybookData>(`/professional/playbooks/${id}`),

  updatePlaybook: (id: string, data: { name: string }) =>
    request<PlaybookData>(`/professional/playbooks/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),

  deletePlaybook: (id: string) =>
    request<void>(`/professional/playbooks/${id}`, { method: 'DELETE' }),

  // Playbook items
  addItem: (playbookId: string, data: { activitySlug: string; config: Record<string, unknown> }) =>
    request<PlaybookItemData>(`/professional/playbooks/${playbookId}/items`, { method: 'POST', body: JSON.stringify(data) }),

  reorderItems: (playbookId: string, ids: string[]) =>
    request<PlaybookItemData[]>(`/professional/playbooks/${playbookId}/items/reorder`, { method: 'PATCH', body: JSON.stringify({ ids }) }),

  updateItem: (itemId: string, config: Record<string, unknown>) =>
    request<PlaybookItemData>(`/professional/playbook-items/${itemId}`, { method: 'PATCH', body: JSON.stringify({ config }) }),

  deleteItem: (itemId: string) =>
    request<void>(`/professional/playbook-items/${itemId}`, { method: 'DELETE' }),

  // Sessions
  startSession: (playbookId: string) =>
    request<PlaybookSessionData>(`/professional/playbooks/${playbookId}/sessions`, { method: 'POST', body: '{}' }),

  getSession: (sessionId: string) =>
    request<PlaybookSessionData>(`/professional/playbook-sessions/${sessionId}`),

  submitItem: (sessionId: string, itemId: string, data: SubmitSessionItemParams) =>
    request<PlaybookSessionItemData>(`/professional/playbook-sessions/${sessionId}/items/${itemId}`, {
      method: 'POST', body: JSON.stringify(data),
    }),

  completeSession: (sessionId: string) =>
    request<PlaybookSessionData>(`/professional/playbook-sessions/${sessionId}/complete`, { method: 'POST', body: '{}' }),

  unlockSession: (sessionId: string, pin: string) =>
    request<PlaybookSessionData>(`/professional/playbook-sessions/${sessionId}/unlock`, {
      method: 'POST', body: JSON.stringify({ pin }),
    }),

  verifyPin: (profileId: string, pin: string) =>
    request<{ ok: boolean }>(`/professional/profiles/${profileId}/verify-pin`, {
      method: 'POST', body: JSON.stringify({ pin }),
    }),

  // Stats
  getProfileStats: (profileId: string) =>
    request<ProfileStats>(`/professional/profiles/${profileId}/stats`),
}
